package chand.wordvoyage.repository

import chand.wordvoyage.config.OpenAIConfig
import chand.wordvoyage.data.ChatMessage
import chand.wordvoyage.data.DetailInfo
import chand.wordvoyage.data.OpenAIChatRequest
import chand.wordvoyage.data.OpenAIChatResponse
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json

class OpenAIRepository(private val apiKey: String = OpenAIConfig.API_KEY) {

    private val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                prettyPrint = true
                isLenient = true
            })
        }
        install(Logging) {
            logger = Logger.DEFAULT
            level = LogLevel.INFO
        }
    }

    suspend fun generateWord(excludeWords: List<String> = emptyList()): Result<String> {
        return try {
            val exclusionClause = if (excludeWords.isNotEmpty()) {
                " Do NOT use any of these already seen words: ${excludeWords.joinToString(", ")}."
            } else ""
            val request = OpenAIChatRequest(
                model = OpenAIConfig.MODEL,
                messages = listOf(
                    ChatMessage(
                        role = "user",
                        content = "Generate one random interesting English word. Only respond with the word, nothing else. Make it creative and unique.$exclusionClause"
                    )
                ),
                maxCompletionTokens = 50
            )

            val url = "${OpenAIConfig.BASE_URL}/chat/completions"

            val response: OpenAIChatResponse = client.post(url) {
                header("Authorization", "Bearer $apiKey")
                contentType(ContentType.Application.Json)
                setBody(request)
            }.body()

            // Debug logging
            println("OpenAI Response: $response")

            // Check for API errors
            if (response.error != null) {
                val errorMsg = "OpenAI API Error: ${response.error.message} (Code: ${response.error.code})"
                println(errorMsg)
                return Result.failure(Exception(errorMsg))
            }

            // Check if choices exist
            if (response.choices == null || response.choices.isEmpty()) {
                val errorMsg = "OpenAI API Error: No choices in response"
                println(errorMsg)
                return Result.failure(Exception(errorMsg))
            }

            // Debug: Print first choice
            println("First choice: ${response.choices.firstOrNull()}")
            println("Message: ${response.choices.firstOrNull()?.message}")
            println("Content: ${response.choices.firstOrNull()?.message?.content}")

            val word = response.choices
                .firstOrNull()
                ?.message
                ?.content
                ?.trim() ?: ""

            if (word.isEmpty()) {
                val errorMsg = "OpenAI API Error: Empty response text. Full response: $response"
                println(errorMsg)
                return Result.failure(Exception(errorMsg))
            }

            Result.success(word)
        } catch (e: Exception) {
            println("OpenAI API Error: ${e.message}")
            e.printStackTrace()
            Result.failure(e)
        }
    }

    suspend fun getWordDetailInfo(word: String): Result<DetailInfo> {
        return try {
            val prompt = """
                Provide the following information for the word '$word':
                
                1. Meaning of the word - provide a clear, concise definition.
                2. How to pronounce the word - provide a simple phonetic pronunciation guide (e.g., "EX-am-pul" for "example").
                3. How the word is commonly used - explain the context and usage.
                4. Two or three example sentences using the word in proper context.
                5. A valid, clickable URL link from a reputable online dictionary or reference site (e.g., Merriam-Webster, Oxford Dictionary, Cambridge Dictionary, or similar). Provide only the direct URL link, no explanatory text.
                
                Return the response strictly in the following JSON format:
                {
                    "meaning": "",
                    "howToPronounce": "",
                    "wordUsage": "",
                    "examplesOfUsingWord": ["", "", ""],
                    "websiteLinkToStillMoreInfo": ""
                }
                
                Important:
                - The howToPronounce field MUST contain a simple phonetic guide that anyone can read aloud.
                - The websiteLinkToStillMoreInfo field MUST contain a valid, working URL that can be opened in a browser.
                - Only the URL should be in the websiteLinkToStillMoreInfo field (no additional text).
                - Only respond with the JSON, nothing else.
            """.trimIndent()

            val request = OpenAIChatRequest(
                model = OpenAIConfig.MODEL,
                messages = listOf(
                    ChatMessage(
                        role = "user",
                        content = prompt
                    )
                ),
                maxCompletionTokens = 300
            )

            val url = "${OpenAIConfig.BASE_URL}/chat/completions"

            val response: OpenAIChatResponse = client.post(url) {
                header("Authorization", "Bearer $apiKey")
                contentType(ContentType.Application.Json)
                setBody(request)
            }.body()

            // Debug logging
            println("OpenAI DetailInfo Response: $response")

            // Check for API errors
            if (response.error != null) {
                val errorMsg = "OpenAI API Error: ${response.error.message} (Code: ${response.error.code})"
                println(errorMsg)
                return Result.failure(Exception(errorMsg))
            }

            // Check if choices exist
            if (response.choices.isNullOrEmpty()) {
                val errorMsg = "OpenAI API Error: No choices in response"
                println(errorMsg)
                return Result.failure(Exception(errorMsg))
            }

            val jsonResponse = response.choices
                .firstOrNull()
                ?.message
                ?.content
                ?.trim() ?: ""

            if (jsonResponse.isEmpty()) {
                val errorMsg = "OpenAI API Error: Empty response text"
                println(errorMsg)
                return Result.failure(Exception(errorMsg))
            }

            // Parse JSON response into DetailInfo object
            println("Parsing JSON response: $jsonResponse")
            val detailInfo = Json.decodeFromString<DetailInfo>(jsonResponse)

            Result.success(detailInfo)
        } catch (e: Exception) {
            println("OpenAI DetailInfo Error: ${e.message}")
            e.printStackTrace()
            Result.failure(e)
        }
    }

    fun close() {
        client.close()
    }
}


